import React, { Component } from 'react';
import { array, bool, func, object, shape, string } from 'prop-types';
import { FormControlLabel, FormGroup, FormLabel, Radio, RadioGroup, TextField } from '@mui/material';
import { Collapse } from '@mui/material';
import borderedFieldStyles from './styles.module.scss';
import { injectIntl } from 'react-intl';
import {
  DIVERSION_POLICIES,
  NOTICE_BODY_MAX_LENGTH,
  NOTICES,
  TEXT_TITLE_MAX_LENGTH,
} from 'constants/Values';
import { SimpleText } from 'util/IntlMessages';
import DiversionSubform from 'components/Notifications/DiversionSubform';
import DestructiveConfirmationDialog from 'components/dialogs/DestructiveConfirmationDialog';
import { canSendDiversion } from 'util/userRoles';

/**
 * Shared controlled content-surface for composing notices.
 * Owns: type radio, header field, body field (hidden when DIVERSION),
 * DiversionSubform (shown when DIVERSION), type-switch guard dialog.
 *
 * Does NOT own: validation rules, save/send actions, recipients,
 * scheduling, Redux state.
 *
 * Parent owns all content state via `value` prop.
 * Parent receives change notifications via `onChange(nextValue)`.
 */
export class MessageEditor extends Component {
  static propTypes = {
    intl: object,
    /** Controlled content value. */
    value: shape({
      header: string.isRequired,
      body: string.isRequired,
      type: string.isRequired,
      diversion: shape({
        preamble: string.isRequired,
        shelters: array.isRequired,
        policy: string.isRequired,
        cancel: bool.isRequired,
      }).isRequired,
    }).isRequired,
    /** Called with the full next value object on any field change. */
    onChange: func.isRequired,
    /** Types to render as radio options (all are subject to userRoles gating for DIVERSION). */
    availableTypes: array.isRequired,
    /** Checked via canSendDiversion(userRoles) to gate the DIVERSION radio. */
    userRoles: array,
    /** Parent-driven inline validation errors for diversion fields. */
    validationErrors: shape({
      diversion: shape({
        preamble: string,
        shelters: string,
      }),
    }),
    /** When true, disables all interactive elements (e.g. save/send in flight). */
    disabled: bool,
    /**
     * Forwarded to DiversionSubform.notificationId — used by the composer's
     * banner-prefilled cancel flow (spec § D9). Template editor passes undefined.
     */
    diversionNotificationId: string,
  };

  static defaultProps = {
    userRoles: [],
    validationErrors: {},
    disabled: false,
    diversionNotificationId: undefined,
    intl: { formatMessage: d => d.id },
  };

  /** Default diversion seed — used when clearing diversion state on type switch. */
  static defaultDiversion = {
    preamble: '',
    shelters: [],
    policy: DIVERSION_POLICIES.NEAREST,
    cancel: false,
  };

  state = {
    /**
     * Holds the candidate next type when the user tries to switch but the guard
     * dialog must confirm first. null when no switch is pending.
     */
    typeSwitchPending: null,
  };

  // ---------------------------------------------------------------------------
  // Dirty-state predicates (spec D1 — type-switch guard semantics)
  // ---------------------------------------------------------------------------

  /**
   * True when the diversion subform has content beyond the empty seed:
   * non-empty preamble, any shelter, non-default policy, or cancel toggled on.
   */
  isDiversionDirty = () => {
    const { diversion } = this.props.value;
    if (!diversion) return false;
    if (diversion.preamble && diversion.preamble.length > 0) return true;
    if (diversion.shelters && diversion.shelters.length > 0) return true;
    if (diversion.policy && diversion.policy !== DIVERSION_POLICIES.NEAREST) return true;
    if (diversion.cancel === true) return true;
    return false;
  };

  /**
   * True when the non-DIVERSION content (header or body) has been typed.
   */
  isOtherTypeDirty = () => {
    const { header, body } = this.props.value;
    return (header && header.length > 0) || (body && body.length > 0);
  };

  // ---------------------------------------------------------------------------
  // Type-switch handlers (spec D1 — guard semantics preserved from NotificationForm)
  // ---------------------------------------------------------------------------

  handleTypeChange = event => {
    const { value: nextType } = event.target;
    const { type } = this.props.value;

    const switchingAwayFromDiversion =
      type === NOTICES.TYPES.DIVERSION && nextType !== NOTICES.TYPES.DIVERSION;
    const switchingIntoDiversion =
      nextType === NOTICES.TYPES.DIVERSION && type !== NOTICES.TYPES.DIVERSION;

    if (switchingAwayFromDiversion && this.isDiversionDirty()) {
      this.setState({ typeSwitchPending: nextType });
      return;
    }
    if (switchingIntoDiversion && this.isOtherTypeDirty()) {
      this.setState({ typeSwitchPending: nextType });
      return;
    }

    // Clean transition — apply directly.
    this.applyTypeSwitch(nextType);
  };

  /**
   * Apply a type switch. Constructs the next full value and calls onChange.
   * Called on clean transitions directly; on dirty transitions after guard confirm.
   */
  applyTypeSwitch = nextType => {
    const { value, onChange } = this.props;
    const updates = { ...value, type: nextType };

    if (nextType === NOTICES.TYPES.DIVERSION) {
      // Switching INTO DIVERSION — clear non-DIVERSION content.
      updates.header = '';
      updates.body = '';
    } else {
      // Switching AWAY from DIVERSION — reset diversion subform to seed.
      updates.diversion = { ...MessageEditor.defaultDiversion };
    }

    onChange(updates);
  };

  handleConfirmTypeSwitch = () => {
    const { typeSwitchPending } = this.state;
    if (typeSwitchPending) {
      this.applyTypeSwitch(typeSwitchPending);
    }
    this.setState({ typeSwitchPending: null });
  };

  handleCancelTypeSwitch = () => {
    this.setState({ typeSwitchPending: null });
  };

  // ---------------------------------------------------------------------------
  // Field change handlers
  // ---------------------------------------------------------------------------

  handleHeaderChange = event => {
    const { value, onChange } = this.props;
    onChange({ ...value, header: event.target.value });
  };

  handleBodyChange = event => {
    const { value, onChange } = this.props;
    onChange({ ...value, body: event.target.value });
  };

  handleDiversionChange = nextDiversion => {
    const { value, onChange } = this.props;
    onChange({ ...value, diversion: nextDiversion });
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  // Splits a "Title: description" i18n string into two lines — bold title,
  // small muted description below. The colon delimiter is consistent across
  // all four response-options strings; if it ever isn't, the whole string
  // falls back to the title slot.
  renderTypeOptionLabel = key => {
    const text = this.props.intl.formatMessage({ id: key });
    const colonIndex = text.indexOf(':');
    if (colonIndex === -1) {
      return <span>{text}</span>;
    }
    const title = text.substring(0, colonIndex);
    const description = text.substring(colonIndex + 1).trim();
    return (
      <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span className={borderedFieldStyles.helperDescription} style={{ marginTop: -2 }}>
          {description}
        </span>
      </span>
    );
  };

  renderTypeSwitchGuardDialog() {
    const { typeSwitchPending } = this.state;
    const {
      intl,
      value: { type },
    } = this.props;
    if (!typeSwitchPending) return null;

    const isAway = type === NOTICES.TYPES.DIVERSION;
    return (
      <DestructiveConfirmationDialog
        open
        header={intl.formatMessage({ id: 'diversion.type-switch-guard.title' })}
        text={intl.formatMessage({
          id: isAway
            ? 'diversion.type-switch-guard.away.text'
            : 'diversion.type-switch-guard.into.text',
        })}
        buttonYes={intl.formatMessage({ id: 'diversion.type-switch-guard.button.confirm' })}
        buttonNo={intl.formatMessage({ id: 'button.cancel' })}
        onConfirm={this.handleConfirmTypeSwitch}
        onCancel={this.handleCancelTypeSwitch}
      />
    );
  }

  render() {
    const {
      value,
      availableTypes,
      userRoles,
      validationErrors,
      disabled,
      diversionNotificationId,
      intl,
    } = this.props;
    const { header, body, type, diversion } = value;
    const isDiversion = type === NOTICES.TYPES.DIVERSION;
    const divErrors = (validationErrors && validationErrors.diversion) || {};

    return (
      <div className="message-editor">
        {/* Type radio — first because selecting the type drives which fields
            appear below. Two-line labels (bold title + small muted description
            on the second line) handle the longer Delay / Poll / Diversion
            descriptions without awkward single-line wraps. */}
        <FormGroup row sx={{ pb: 2 }}>
          <FormLabel component="legend">
            <SimpleText id="notifications.send-form.form.legend.notification-response" />
          </FormLabel>
          <RadioGroup
            aria-label="response-options"
            name="response-options"
            value={type}
            onChange={this.handleTypeChange}
          >
            {availableTypes.includes(NOTICES.TYPES.GENERAL) && (
              <FormControlLabel
                value={NOTICES.TYPES.GENERAL}
                control={<Radio color="primary" />}
                label={this.renderTypeOptionLabel(
                  'notifications.send-form.response-options.no-response',
                )}
                disabled={disabled}
              />
            )}
            {availableTypes.includes(NOTICES.TYPES.DELAY) && (
              <FormControlLabel
                value={NOTICES.TYPES.DELAY}
                control={<Radio color="primary" />}
                label={this.renderTypeOptionLabel(
                  'notifications.send-form.response-options.delay',
                )}
                disabled={disabled}
              />
            )}
            {availableTypes.includes(NOTICES.TYPES.SECURITY) && (
              <FormControlLabel
                value={NOTICES.TYPES.SECURITY}
                control={<Radio color="primary" />}
                label={this.renderTypeOptionLabel(
                  'notifications.send-form.response-options.security',
                )}
                disabled={disabled}
              />
            )}
            {availableTypes.includes(NOTICES.TYPES.DIVERSION) &&
              canSendDiversion(userRoles) && (
                <FormControlLabel
                  value={NOTICES.TYPES.DIVERSION}
                  control={<Radio color="primary" />}
                  label={this.renderTypeOptionLabel(
                    'notifications.send-form.response-options.diversion',
                  )}
                  disabled={disabled}
                />
              )}
          </RadioGroup>
        </FormGroup>

        {/* Header field — always rendered */}
        <FormGroup row sx={{ pb: 2 }}>
          <div className={borderedFieldStyles.borderedField}>
            <TextField
              id="message-editor-header"
              label={intl.formatMessage({ id: 'dialog.notices.form.title' })}
              value={header}
              onChange={this.handleHeaderChange}
              margin="dense"
              fullWidth
              disabled={disabled}
              slotProps={{ htmlInput: { maxLength: TEXT_TITLE_MAX_LENGTH } }}
              helperText={`${header.length}/${TEXT_TITLE_MAX_LENGTH}`}
            />
          </div>
        </FormGroup>

        {/* Body / DiversionSubform — mutually exclusive. Both wrapped in
            <Collapse> so the type-switch morph is a smooth height + opacity
            transition rather than an instant DOM swap. */}
        <Collapse in={!isDiversion} mountOnEnter>
          <FormGroup row sx={{ pb: 2 }}>
            <div className={borderedFieldStyles.borderedField}>
              <TextField
                id="message-editor-body"
                label={intl.formatMessage({ id: 'dialog.notices.form.text' })}
                multiline
                rows="6"
                value={body}
                onChange={this.handleBodyChange}
                margin="dense"
                fullWidth
                disabled={disabled}
                slotProps={{ htmlInput: { maxLength: NOTICE_BODY_MAX_LENGTH } }}
                helperText={`${body.length}/${NOTICE_BODY_MAX_LENGTH}`}
              />
            </div>
          </FormGroup>
        </Collapse>

        <Collapse in={isDiversion} mountOnEnter>
          <FormGroup row sx={{ pb: 2 }}>
            <DiversionSubform
              value={diversion}
              onChange={this.handleDiversionChange}
              notificationId={diversionNotificationId}
              validationErrors={divErrors}
              disabled={disabled}
            />
          </FormGroup>
        </Collapse>

        {/* Type-switch guard dialog */}
        {this.renderTypeSwitchGuardDialog()}
      </div>
    );
  }
}

export default injectIntl(MessageEditor);
